import { useState, type FormEvent } from 'react';
import { Loader2, Plus, ShieldCheck, UserCircle2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useUsers, useCreateUser, type CreateUserInput } from '@/hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import type { UserRole } from '@/types';

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrador',
  recruiter: 'Recrutador',
  viewer: 'Visualizador',
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: 'Acesso total, incluindo gestão de usuários',
  recruiter: 'Cria e edita candidatos, interações e comentários',
  viewer: 'Apenas leitura',
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const { data: users, isLoading, error } = useUsers();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-lg rounded border border-border bg-bg-surface p-6">
          <h1 className="mb-2 font-serif text-xl text-text-primary">Acesso restrito</h1>
          <p className="text-sm text-text-secondary">
            Apenas administradores podem gerenciar usuários. Peça a um admin para te dar acesso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-serif text-2xl text-text-primary">Usuários</h1>
            <p className="mt-1 text-sm text-text-secondary">
              Gerencie quem tem acesso ao sistema.
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Novo usuário
          </Button>
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
          </div>
        )}

        {error && (
          <p className="rounded-sm border border-status-red/25 bg-status-red/5 px-3 py-2 text-xs text-status-red">
            Falha ao carregar usuários: {error instanceof Error ? error.message : 'erro desconhecido'}
          </p>
        )}

        {users && users.length > 0 && (
          <div className="overflow-hidden rounded border border-border bg-bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-bg-elevated/40">
                <tr className="text-left text-2xs uppercase tracking-wider text-text-muted">
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Papel</th>
                  <th className="px-4 py-3 font-medium">Criado em</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.role === 'admin' ? (
                          <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                        ) : (
                          <UserCircle2 className="h-3.5 w-3.5 text-text-muted" />
                        )}
                        <span className="text-text-primary">{u.name}</span>
                        {u.id === currentUser?.id && (
                          <span className="text-2xs text-text-muted">(você)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                        {ROLE_LABELS[u.role]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {format(parseISO(u.created_at), "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {users && users.length === 0 && (
          <p className="rounded border border-border bg-bg-surface px-4 py-6 text-center text-sm text-text-secondary">
            Nenhum usuário cadastrado ainda.
          </p>
        )}
      </div>

      <CreateUserDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateUserDialog({ open, onOpenChange }: DialogProps) {
  const { toast } = useToast();
  const createUser = useCreateUser();

  const [form, setForm] = useState<CreateUserInput>({
    name: '',
    email: '',
    password: '',
    role: 'recruiter',
  });
  const [error, setError] = useState('');

  function reset() {
    setForm({ name: '', email: '', password: '', role: 'recruiter' });
    setError('');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }

    try {
      await createUser.mutateAsync({
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      });
      toast({
        variant: 'success',
        title: 'Usuário criado',
        description: `${form.name} foi adicionado como ${ROLE_LABELS[form.role]}.`,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao criar usuário';
      setError(message);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo usuário</DialogTitle>
          <DialogDescription>
            Cadastre um membro da equipe. Ele fará login com o email e senha definidos aqui.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-text-secondary">
              Nome
            </label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Ana Souza"
            />
          </div>

          <div>
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-text-secondary">
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="ana.souza@nobelcapital.com.br"
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-text-secondary">
              Senha inicial
            </label>
            <Input
              id="password"
              type="text"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="mínimo 8 caracteres"
            />
            <p className="mt-1 text-2xs text-text-muted">
              Compartilhe a senha com o usuário. Ele pode alterá-la depois em "Esqueceu a senha?".
            </p>
          </div>

          <div>
            <label htmlFor="role" className="mb-1.5 block text-xs font-medium text-text-secondary">
              Papel
            </label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v as UserRole })}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['admin', 'recruiter', 'viewer'] as UserRole[]).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-2xs text-text-muted">{ROLE_DESCRIPTIONS[form.role]}</p>
          </div>

          {error && (
            <p className="rounded-sm border border-status-red/25 bg-status-red/5 px-3 py-2 text-xs text-status-red">
              {error}
            </p>
          )}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {createUser.isPending ? 'Criando…' : 'Criar usuário'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
